const DEFAULT_LOCALE = 'en-US';
const KC_TZ = 'America/Chicago';

/**
 * Format time safely in Kansas City local time and show timezone abbreviation.
 * Returns a string like "3:45 PM CST" or "8:15 AM CDT".
 */
export function fmtTime(iso, { locale = DEFAULT_LOCALE, timeZone = KC_TZ } = {}) {
  const d = new Date(iso.replace('Z', ''));
  if (Number.isNaN(d.getTime())) return '';
  return `${formatTimeText(d)} ${getTimezone(iso, { locale, timeZone })}`;
  // Display hour/minute + timezone abbreviation
  // return new Intl.DateTimeFormat(locale, {
  //   hour: 'numeric',
  //   minute: '2-digit',
  //   timeZoneName: 'short',
  //   timeZone,
  // }).format(d);
}

/**
 * 
 * @param {*} time string in the format of HH:mm:ss
 * @param {*} date datetime object without time stamp considerations
 * @param {*} param2 
 * @returns string like "3:45 PM CST" or "8:15 AM CDT".
 */
export function fmtTimeFromString(time, date, { locale = DEFAULT_LOCALE, timeZone = KC_TZ } = {}) {

  if (!time || !date ) return '';

  const [hours, minutes] = time.split(':').map(Number);

  const hours12 = hours % 12 || 12;
  const ampm =  hours < 12 ? "AM" : "PM";

  const timeZoneString = new Intl.DateTimeFormat(locale, {
    timeZoneName: 'short',
    timeZone,
  }).formatToParts(date).find(x => x.type === 'timeZoneName').value;

  //onsole.log("fmtTimeFromString", timeZoneString);

  // Display hour/minute + timezone abbreviation
  return `${hours12}:${minutes<10 ? "0":""}${minutes} ${ampm} ${timeZoneString}`;
}

/**
 * Get timezone abbreviation for a given date/time.
 * Returns a string like "CST" or "CDT".
 */
export function getTimezone(iso, { locale = DEFAULT_LOCALE, timeZone = KC_TZ } = {}) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const parts = new Intl.DateTimeFormat(locale, {
    timeZoneName: 'short',
    timeZone,
  }).formatToParts(d);

  const tzPart = parts.find((p) => p.type === 'timeZoneName');
  return tzPart ? tzPart.value : '';
}

/**
 * Return { mon, day, dateTime, tzAbbr } for event badges + semantic <time>.
 * Adds timezone abbreviation so you can display or debug it.
 */
export function monthDay(iso, { locale = DEFAULT_LOCALE, timeZone = KC_TZ } = {}) {
  const d = new Date(iso);

  // Validate date before formatting
  if (Number.isNaN(d.getTime())) {
    return {
      mon: '',
      day: '',
      dateTime: '',
      tzAbbr: '',
    };
  }

  return {
    mon: new Intl.DateTimeFormat(locale, { month: 'short', timeZone }).format(d).toUpperCase(),
    day: new Intl.DateTimeFormat(locale, { day: '2-digit', timeZone }).format(d).replace(/^0/, ''),
    dateTime: d.toISOString(),
    tzAbbr: getTimezone(iso, { locale, timeZone }),
    month: new Intl.DateTimeFormat(locale, { month: 'long', timeZone }).format(d),
  };
}

export function fmtDate(date) {
  const d = new Date(date);
  const formatted = d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });

  return formatted;
}

/**
 * Format time without timezone abbreviation.
 * Returns a string like "3:45 PM" or "8:15 AM".
 */
export function fmtTimeOnly(iso) {
  const d = new Date(iso.replace('Z', ''));
  if (Number.isNaN(d.getTime())) return '';

  return formatTimeText(d);
}


/**
 * Format time without timezone abbreviation.
 * Returns a string like "3:45 PM" or "8:15 AM".
 */
export function fmtTimeOnlyFromString(time) {
  if (!time) return '';

  const [hours, minutes] = time.split(':').map(Number);

  const hours12 = hours % 12 || 12;
  const ampm =  hours < 12 ? "AM" : "PM";

  return `${hours12}:${minutes<10 ? "0":""}${minutes} ${ampm}`;
}

/**
 * Format a time range with timezone at the end.
 * Returns a string like "12:00 PM - 5:00 PM CST".
 */
export function fmtTimeRange(startIso, endIso, { locale = DEFAULT_LOCALE, timeZone = KC_TZ } = {}) {
  const startTime = fmtTimeOnly(startIso);
  const endTime = fmtTimeOnly(endIso);
  const tz = getTimezone(startIso, { locale, timeZone });

  return `${startTime} - ${endTime} ${tz}`;
}


/**
 * Format a time range with timezone at the end.
 * Returns a string like "12:00 PM - 5:00 PM CST".
 */
export function fmtTimeRangeFromStrings(startString, endString, date, { locale = DEFAULT_LOCALE, timeZone = KC_TZ } = {}) {
  const startTime = fmtTimeOnlyFromString(startString, { locale, timeZone });
  const endTime = fmtTimeOnlyFromString(endString, { locale, timeZone });
  const tz = getTimezone(date, { locale, timeZone });

  return `${startTime} - ${endTime} ${tz}`;
}

/**
 * Check if two dates are on the same day (ignoring time).
 */
export function isSameDay(date1, date2) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

export function formatAMPM(date) {
  let hours = date.getHours();
  let minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours %= 12;
  hours = hours || 12; // the hour '0' should be '12'
  minutes = minutes < 10 ? `0${minutes}` : minutes;
  const strTime = `${hours}:${minutes} ${ampm}`;
  return strTime;
}

/**
 * Get today's date in YYYY-MM-DD format (for HTML date inputs).
 * Returns a string like "2024-01-15".
 */
export function getTodayDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDateTime(dateInput) {
  if (!dateInput) return '';

  // If timezone missing, treat as UTC
  let normalizedInput = dateInput;
  if (typeof dateInput === 'string' && !dateInput.endsWith('Z') && !dateInput.includes('+')) {
    normalizedInput = dateInput + 'Z';
  }

  const date = new Date(normalizedInput);

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  let hours = date.getHours();
  let minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours %= 12;
  hours = hours || 12;
  minutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
}

export function formatTimeText(dateInput) {
  if (!dateInput) return '';

  // If timezone missing, treat as UTC
  let normalizedInput = dateInput;
  if (typeof dateInput === 'string' && !dateInput.endsWith('Z') && !dateInput.includes('+')) {
    normalizedInput = dateInput + 'Z';
  }

  const date = new Date(normalizedInput);

  let hours = date.getHours();
  let minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours %= 12;
  hours = hours || 12;
  minutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${hours}:${minutes} ${ampm.toUpperCase()}`;
}

/**
 * Combine date and time fields into ISO datetime string
 * @param {string} date - ISO date string
 * @param {string|null} time - Time string in "HH:mm:ss" format
 * @returns {string} ISO datetime string
 */
export function combineDateAndTime(date, time, returnString = true) {
  const dateObj = new Date(date);

  if (time) {
    const [hours, minutes, seconds] = time.split(':').map(Number);
    dateObj.setHours(hours || 0, minutes || 0, seconds || 0, 0);
  }

  if (returnString)
    return dateObj.toISOString();
  else 
    return dateObj;
}

